import { useEffect, useRef, useState, useActionState } from 'react';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import CustomDialog from '@/components/ui/Dialog/Dialog';
import popupEmail from '@/assets/icons/popup-email.svg';
import successs from '@/assets/icons/success.svg';
import fileAttach from '@/assets/icons/file-attach.svg';
import { usePutAuthApi } from '@/hooks/UseApiCalls';
import { ReceiptToSendDtoOut } from '@/models/out/ReceiptToSendDtoOut';
import { ReceiptsEmailResultDtoIn } from '@/models/in/ReceiptsEmailResultDtoIn';
import SkippedReceiptsList from './SkippedReceiptsList';
import styles from './ReceiptEmailDialog.module.css';

interface ReceiptEmailDialogProps {
  receipts: ReceiptToSendDtoOut[];
  defaultEmail?: string;
  isSendReceiptOpen: boolean;
  setIsSendReceiptOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

// A failed send says which receipts it could not send and why (HAT-1444); a failure of any other kind has no such list
const resultOfFailedSend = (error: unknown): ReceiptsEmailResultDtoIn | null => {
  const data = (error as AxiosError<ReceiptsEmailResultDtoIn>)?.response?.data;
  return Array.isArray(data?.skipped) ? data : null;
};

const ReceiptEmailDialog = ({ receipts, defaultEmail, isSendReceiptOpen, setIsSendReceiptOpen }: ReceiptEmailDialogProps) => {
  const { t } = useTranslation();
  const [emailAddress, setEmailAddress] = useState<string>('');
  const [isOtherAddress, setIsOtherAddress] = useState<boolean>(false);
  const [isSendSuccessfulOpen, setIsSendSuccessfulOpen] = useState<boolean>(false);
  const [isReceiptUnavailable, setIsReceiptUnavailable] = useState<boolean>(false);
  const [sendResult, setSendResult] = useState<ReceiptsEmailResultDtoIn | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  // A single receipt is sent to the donor's address unless another one was typed in
  const isDefaultEmailUsed = !!defaultEmail && !isOtherAddress && !emailAddress;
  const recipient = isDefaultEmailUsed ? defaultEmail : emailAddress;

  useEffect(() => {
    if (isSendReceiptOpen) {
      setIsOtherAddress(false);
      setEmailAddress('');
    }
  }, [isSendReceiptOpen]);

  const [firstReceipt] = receipts;
  // Also for a single receipt: unlike receipts/{id}/send-email, this answer says why a receipt was left out (HAT-1444)
  const { mutateAsync: sendReceiptsToEmail } = usePutAuthApi<ReceiptsEmailResultDtoIn, void, ReceiptToSendDtoOut[]>(
    `receipts/send-multiple-email?recipient=${encodeURIComponent(recipient)}`
  );

  const [, sendReceiptEmail, isEmailSending] = useActionState(async () => {
    if (!recipient || receipts.length === 0) {
      return;
    }
    try {
      setSendResult(await sendReceiptsToEmail({ body: receipts }));
      setIsSendSuccessfulOpen(true);
      setIsSendReceiptOpen(false);
      setEmailAddress('');
    } catch (error) {
      setSendResult(resultOfFailedSend(error));
      setIsReceiptUnavailable(true);
      setIsSendReceiptOpen(false);
      setEmailAddress('');
      console.error('Error sending receipt(s):', error);
    }
  }, null);

  // The donor-address flow keeps a short "Send" label, also after switching to another address
  const sendBtnText = defaultEmail ? t('sendAction') : receipts.length > 1 ? t('sendReceipts') : t('sendReceipt');

  const skippedReceipts = sendResult?.skipped ?? [];
  const sentCount = sendResult?.sentReceiptIds.length ?? 0;
  // With the reasons at hand the title says what happened; without them (any other failure) it stays general
  const unavailableTitle =
    skippedReceipts.length > 0
      ? t(receipts.length === 1 ? 'receiptNotSent' : 'receiptsNotSent')
      : t(receipts.length === 1 ? 'receiptUnavailable' : 'oneOrMoreReceiptsUnavailable');

  return (
    <>
      <CustomDialog
        open={isSendReceiptOpen && !isReceiptUnavailable}
        setOpen={setIsSendReceiptOpen}
        btnText={isEmailSending ? `${t('sending')}...` : sendBtnText}
        onBtnClick={(e) => {
          e.preventDefault();
          formRef.current?.requestSubmit();
        }}
        secondaryBtnText={isDefaultEmailUsed ? t('sendToAnotherAddress') : undefined}
        onSecondaryBtnClick={() => setIsOtherAddress(true)}
        secondaryBtnDisabled={isEmailSending}
      >
        <img src={popupEmail} alt="" />
        <div className={styles.dialogContentContainer}>
          <h2 className="heading-h2-bold">
            {receipts.length > 1 ? t('sendReceipts') : t('sendReceipt')} {t('to')}
            {t('email')}
          </h2>
          <p className="body-lr-regular text-(--font-body-1)">
            {receipts.length > 1 ? t('receipts') : t('receiptNumber')}{' '}
            <span className="body-lr-bold">{receipts.length > 1 ? `(${receipts.length})` : firstReceipt?.receiptId}</span>
          </p>
        </div>
        <form ref={formRef} className={styles.emailFormContainer} action={sendReceiptEmail}>
          {isDefaultEmailUsed ? (
            <>
              <span className="body-sm-regular text-(--font-body-1)">{t('email')}</span>
              <p className={`${styles.emailValue} body-md-regular text-(--font-body-1)`} dir="ltr">
                {defaultEmail}
              </p>
            </>
          ) : (
            <>
              <label htmlFor="email" className="body-sm-regular text-(--font-body-1)">
                {t('email')}
              </label>
              <input
                id="email"
                type="email"
                className={styles.emailInput}
                value={emailAddress}
                required
                autoFocus={isOtherAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
              />
            </>
          )}
        </form>
      </CustomDialog>

      <CustomDialog open={isReceiptUnavailable} setOpen={setIsReceiptUnavailable} btnText={t('closeWindow')}>
        <img src={fileAttach} alt="" />
        <h2 className="heading-h2-bold">{unavailableTitle}</h2>
        <SkippedReceiptsList skipped={skippedReceipts} isSingleReceipt={receipts.length === 1} />
      </CustomDialog>

      <CustomDialog open={isSendSuccessfulOpen} setOpen={setIsSendSuccessfulOpen} btnText={t('closeWindow')}>
        <img src={successs} alt="" />
        {skippedReceipts.length > 0 ? (
          <>
            <h2 className="heading-h2-bold">{t('receiptsSentPartially', { count: sentCount, total: sentCount + skippedReceipts.length })}</h2>
            <SkippedReceiptsList skipped={skippedReceipts} />
          </>
        ) : (
          <h2 className="heading-h2-bold">{receipts.length > 1 ? t('receiptsSentSuccessfully') : t('receiptSentSuccessfully')}!</h2>
        )}
      </CustomDialog>
    </>
  );
};

export default ReceiptEmailDialog;
